
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type CloudVolumesShaderUniforms = {
	uTaaFrameIndex: WebGLUniformLocation,
	uDepthTexture: WebGLUniformLocation,
	uInvViewProjection: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uTime: WebGLUniformLocation,
	uSunDirection: WebGLUniformLocation,
	uSunIntensity: WebGLUniformLocation,
	uTintColor: WebGLUniformLocation,
	uFogColor: WebGLUniformLocation,
	uHorizonMist: WebGLUniformLocation,
	uExposure: WebGLUniformLocation,
	uCloudColor: WebGLUniformLocation,
	uVolumeCount: WebGLUniformLocation,
	uVolumeMin: WebGLUniformLocation[],
	uVolumeMax: WebGLUniformLocation[],
	uVolumeParams: WebGLUniformLocation[],
	uVolumeWind: WebGLUniformLocation[],
	uVolumePolygonStart: WebGLUniformLocation[],
	uVolumePolygonCount: WebGLUniformLocation[],
	uPolygonEdges: WebGLUniformLocation[]
};

export type CloudVolumesShaderAttributes = {
	aVertexPosition: number,
	vTexCoord: number
};

export type CloudVolumesShaderSettings = string[];

export class CloudVolumesShader extends ShaderBase<CloudVolumesShaderUniforms, CloudVolumesShaderAttributes> {

    private static fragmentShaderSource = `
// Volumetric cloud volumes: raymarches 3D FBM noise inside per-volume
// AABB-clipped ray segments. Horizontal extent is the authored polygon
// (signed distance computed in-shader from per-edge uniforms, domain-warped
// by FBM noise for organic silhouettes); vertical extent is the \`[baseY,
// topY]\` band. Atmosphere matching is per-fragment via a single
// \`sampleAtmosphereTone\` call at scene distance; cloud body is white-ish
// modulated by sun direction and day/night.
//
// Composition: outputs \`vec4(litColor, density)\` and uses premultiplied
// alpha blend (\`ONE\`, \`ONE_MINUS_SRC_ALPHA\`) so the GPU composites against
// whatever is in the target FBO. This sidesteps the scratch-FBO+blit
// pattern used by AerialPerspectivePass — the editor renders to the RGBA8
// canvas (no HDR intermediate) and blitting from RGBA8 to RGBA16F silently
// fails, leaving the scratch zeroed.

// --- begin: world_pos_from_depth.glsl ---
// Reconstruct world / view position from a 24-bit depth sample, screen-space
// UV, and an inverse projection matrix. Replaces an explicit RGBA32F position
// attachment in the G-buffer (~16 bytes/pixel saved).
//
// Reversed-Z: the depth buffer maps near → 1.0 and far → 0.0. Cleared depth
// (= 0.0) means "no geometry was drawn" — callers should discard or treat this
// as background BEFORE calling these functions. The reconstruction itself
// still works at depth == 0.0 (it returns the far-plane point), but the
// result is rarely useful for lighting/AO/decals.
//
// The math is reversal-agnostic: invViewProjection is the inverse of whichever
// projection the depth buffer was rendered with, and the perspective divide
// recovers the correct world position regardless of which side of the NDC z
// range each plane maps to.

// Inputs:
//   depth              — raw [0,1] value from texture(uDepthMap, uv).r
//   uv                 — screen-space UV (vTexCoord, in [0,1])
//   invViewProjection  — inverse of (projection * view) for the current camera
vec3 worldPosFromDepth(float depth, vec2 uv, mat4 invViewProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 world = invViewProjection * ndc;
	return world.xyz / world.w;
}

// View-space variant — pass invProjection (inverse of camera.projectionMatrix)
// when you only need view space. Skips the worldPos → viewPos round-trip,
// which matters for SSAO that samples 16+ positions per fragment.
vec3 viewPosFromDepth(float depth, vec2 uv, mat4 invProjection) {
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = invProjection * ndc;
	return view.xyz / view.w;
}

// --- end: world_pos_from_depth.glsl ---
// --- begin: temporal_jitter.glsl ---
// Shared per-frame rotation helper for variance-fed temporal effects (PCF
// shadow taps, SSAO hemisphere orientation, etc.). The same rotation is used
// in multiple places so they stay coherent — see project_temporal_pcf_shadows
// memory for the underlying pattern.

#ifndef TEMPORAL_JITTER_INCLUDED
#define TEMPORAL_JITTER_INCLUDED

// Per-frame rotation index, sourced from Camera.taaFrameIndex. 0 means "TAA
// off, no temporal jitter" — the helper returns identity so consumers fall
// back bit-identically to their pre-temporal sampling pattern. Nonzero
// advances the rotation; combined with the per-pixel hash below this gives
// every pixel a different rotation every frame, and TAA's reprojected-history
// blend averages the resulting angular variance into supersampled output.
uniform int uTaaFrameIndex;

// Build a 2D rotation matrix mixing per-frame and per-pixel terms. Per-pixel
// IGN dominates (×16 weight) so neighbouring pixels see distinct angles
// within a single frame — that keeps TAA's 3×3 neighborhood AABB wide enough
// to not clamp the temporal change. Per-frame term decorrelates successive
// frames so TAA averages distinct sample patterns. Returns identity when the
// index is zero, which is the only path taken when TAA is off.
mat2 temporal_jitter_matrix() {
	if (uTaaFrameIndex == 0) {
		return mat2(1.0, 0.0, 0.0, 1.0);
	}
	// Interleaved gradient noise (Jiminez 2014) — cheap blue-noise-ish hash.
	float ign = fract(52.9829189 * fract(0.06711056 * gl_FragCoord.x + 0.00583715 * gl_FragCoord.y));
	float a = (float(uTaaFrameIndex) + ign * 16.0) * 2.39996323;
	float c = cos(a);
	float s = sin(a);
	return mat2(c, s, -s, c);
}

#endif // TEMPORAL_JITTER_INCLUDED

// --- end: temporal_jitter.glsl ---
// --- begin: atmosphere.glsl ---
// Single-scattering Rayleigh + Mie integration through a spherical atmosphere
// shell. Shared by:
//   - procedural-sky: sky dome colour as a function of view direction.
//   - aerial-perspective: distance-based haze that blends opaque scene pixels
//     toward the same scattering value, so far-LOD shape drift, tile-edge
//     mismatches, and the hard horizon clip all wash into atmosphere.
//
// Inputs use the well-known Nishita / "glsl-atmosphere" formulation. The
// virtual observer sits just above the planet surface — the shader's actual
// world position doesn't matter because scattering is a pure function of the
// view direction relative to the planet centre.

const float ATMOSPHERE_PI = 3.14159265358979;

const float PLANET_RADIUS = 6371000.0;
const float ATMOS_RADIUS  = 6471000.0;
const vec3  RAY_BETA      = vec3(5.5e-6, 13.0e-6, 22.4e-6); // Rayleigh scattering coefficients
const vec3  MIE_BETA      = vec3(21e-6);                    // Mie scattering coefficient
const float HEIGHT_RAY    = 8000.0;
const float HEIGHT_MIE    = 1200.0;
const float G             = 0.758; // Mie phase asymmetry

const int STEPS_PRIMARY = 16;
const int STEPS_LIGHT   = 8;

// Returns t for the far intersection of a ray with a sphere centred at the
// origin, or -1.0 if there is no intersection.
float raySphereFar(vec3 origin, vec3 dir, float radius) {
	float b = dot(origin, dir);
	float c = dot(origin, origin) - radius * radius;
	float d = b * b - c;
	if (d < 0.0) return -1.0;
	return -b + sqrt(d);
}

// Atmospheric scattering integrated along the primary ray, capped at maxDist
// metres. The sky dome passes a huge value (full column to outer atmosphere);
// aerial perspective passes the camera-to-pixel distance so opaque surfaces
// only receive in-scattering from the air actually between camera and
// surface — without the cap a 2km mountain peak picks up the same Mie sun
// peak as the 100km sky behind it. Caller multiplies by uSunIntensity.
vec3 atmosphere(vec3 rayDir, vec3 rayOrigin, vec3 sunDir, float maxDist) {
	float atmosDist = raySphereFar(rayOrigin, rayDir, ATMOS_RADIUS);
	if (atmosDist < 0.0) return vec3(0.0);
	atmosDist = min(atmosDist, maxDist);

	float stepSize = atmosDist / float(STEPS_PRIMARY);

	float mu = dot(rayDir, sunDir);
	float mu2 = mu * mu;
	float gg = G * G;
	float phaseRay = 3.0 / (16.0 * ATMOSPHERE_PI) * (1.0 + mu2);
	float phaseMie = 3.0 / (8.0 * ATMOSPHERE_PI)
		* ((1.0 - gg) * (mu2 + 1.0))
		/ (pow(1.0 + gg - 2.0 * mu * G, 1.5) * (2.0 + gg));

	vec3 sumRay = vec3(0.0);
	vec3 sumMie = vec3(0.0);
	float odRay = 0.0;
	float odMie = 0.0;

	for (int i = 0; i < STEPS_PRIMARY; i++) {
		vec3 sp = rayOrigin + rayDir * (float(i) + 0.5) * stepSize;
		float h = length(sp) - PLANET_RADIUS;
		float hr = exp(-h / HEIGHT_RAY) * stepSize;
		float hm = exp(-h / HEIGHT_MIE) * stepSize;
		odRay += hr;
		odMie += hm;

		// Light ray from sample point toward the sun.
		float lDist = raySphereFar(sp, sunDir, ATMOS_RADIUS);
		float lStep = lDist / float(STEPS_LIGHT);
		float lOdRay = 0.0;
		float lOdMie = 0.0;
		bool blocked = false;
		for (int j = 0; j < STEPS_LIGHT; j++) {
			vec3 lp = sp + sunDir * (float(j) + 0.5) * lStep;
			float lh = length(lp) - PLANET_RADIUS;
			if (lh < 0.0) { blocked = true; break; }
			lOdRay += exp(-lh / HEIGHT_RAY) * lStep;
			lOdMie += exp(-lh / HEIGHT_MIE) * lStep;
		}

		if (!blocked) {
			vec3 tau = RAY_BETA * (odRay + lOdRay) + MIE_BETA * 1.1 * (odMie + lOdMie);
			vec3 attn = exp(-tau);
			sumRay += hr * attn;
			sumMie += hm * attn;
		}
	}

	return phaseRay * RAY_BETA * sumRay + phaseMie * MIE_BETA * sumMie;
}

// --- end: atmosphere.glsl ---

#define MAX_VOLUMES 8
#define MAX_EDGES_TOTAL 96

in vec2 vTexCoord;
out vec4 fragColor;

uniform sampler2D uDepthTexture;
uniform mat4 uInvViewProjection;
uniform vec3 uCameraPosition;
uniform float uTime;

// Atmosphere matching: sample _shared/atmosphere.glsl at ray-volume entry +
// exit (cheap — twice per fragment per volume hit) and lerp inside the
// raymarch loop. Per-step sampling would be 12× this cost; lerping between
// two anchor points is visually identical in practice and what aerial
// perspective uses for distance haze. All five tint/exposure/mist params
// mirror the sky shader so cloud colour stays in lock-step with the dome
// behind it through day-night transitions.
uniform vec3 uSunDirection;   // direction toward the sun, normalized
uniform float uSunIntensity;
uniform vec3 uTintColor;
uniform vec3 uFogColor;
uniform float uHorizonMist;
uniform float uExposure;
// Albedo-style multiplier for the cloud body. At 1.0 clouds reflect the full
// atmospheric scattering value back to the camera; the engine default is
// pure white, but authoring-time tinting could be reused via the existing
// \`cloudColor\` slider.
uniform vec3 uCloudColor;

uniform int uVolumeCount;
// Packed per-volume data, indexed [0, 8):
//   uVolumeMin[i].xyz = world-space AABB min, .w = baseY (redundant with .y today)
//   uVolumeMax[i].xyz = world-space AABB max, .w = topY  (redundant with .y today)
//   uVolumeParams[i] = (density, coverage, edgeFeather, noiseScale)
//   uVolumeWind[i]   = (windDirX, windDirZ, windSpeed, _)
//   uVolumePolygonStart[i] / uVolumePolygonCount[i] index into uPolygonEdges
uniform vec4 uVolumeMin[8];
uniform vec4 uVolumeMax[8];
uniform vec4 uVolumeParams[8];
uniform vec4 uVolumeWind[8];
uniform int uVolumePolygonStart[8];
uniform int uVolumePolygonCount[8];

// Flat edge array shared across volumes. Each vec4 is one polygon edge:
// (vert.x, vert.y, nextVert.x, nextVert.y). The uploader packs all volumes'
// edges back-to-back; (start, count) pairs above carve out each volume's
// slice. Capped at MAX_EDGES_TOTAL across all volumes — overflow truncates
// the offending polygon (logged on CPU).
uniform vec4 uPolygonEdges[96];

const int RAYMARCH_STEPS = 12;
const float SKY_DIST = 100000.0;
// Wind drift is applied AFTER the noiseScale multiply so windOffset operates
// in noise-space directly (1 noise unit ~ one feature wavelength). Without
// this scaling, wind motion at default windSpeed ~1.5 m/s would shift the
// noise field by only ~0.03 noise units/sec — perceptually frozen. 0.2 turns
// windSpeed=1 into 0.2 wavelengths/sec drift (visibly flowing within ~3s);
// windSpeed=10 (slider max) is a strong gust.
const float WIND_NOISE_SCALE = 0.2;

// --- Noise ---

float hash3(vec3 p) {
	p = fract(p * vec3(443.8975, 397.2973, 491.1871));
	p += dot(p.yxz, p.xyz + 19.19);
	return fract(p.x * p.y * p.z);
}

float valueNoise(vec3 p) {
	vec3 i = floor(p);
	vec3 f = fract(p);
	vec3 u = f * f * (3.0 - 2.0 * f);
	float n000 = hash3(i + vec3(0.0, 0.0, 0.0));
	float n100 = hash3(i + vec3(1.0, 0.0, 0.0));
	float n010 = hash3(i + vec3(0.0, 1.0, 0.0));
	float n110 = hash3(i + vec3(1.0, 1.0, 0.0));
	float n001 = hash3(i + vec3(0.0, 0.0, 1.0));
	float n101 = hash3(i + vec3(1.0, 0.0, 1.0));
	float n011 = hash3(i + vec3(0.0, 1.0, 1.0));
	float n111 = hash3(i + vec3(1.0, 1.0, 1.0));
	float nx00 = mix(n000, n100, u.x);
	float nx10 = mix(n010, n110, u.x);
	float nx01 = mix(n001, n101, u.x);
	float nx11 = mix(n011, n111, u.x);
	float nxy0 = mix(nx00, nx10, u.y);
	float nxy1 = mix(nx01, nx11, u.y);
	return mix(nxy0, nxy1, u.z);
}

// 3-octave FBM for the density field. The fine-frequency octave gives the
// inside-cloud surface detail that makes the volume read as fractal puffs
// rather than smooth blobs.
float fbm3(vec3 p) {
	float v = 0.0;
	float a = 0.5;
	for (int i = 0; i < 3; i++) {
		v += a * valueNoise(p);
		p *= 2.03;
		a *= 0.5;
	}
	return v;
}

// 2-octave FBM used only for the silhouette displacement. The high-frequency
// detail there is wasted — the density FBM already adds fine texture at the
// edges; the silhouette only needs the low-frequency lobes that carve out
// large inlets/extensions. Saves ~150 ops per raymarch step on cloud pixels.
float fbm2(vec3 p) {
	float v = 0.5 * valueNoise(p);
	v += 0.25 * valueNoise(p * 2.03);
	return v;
}

// --- Geometry ---

vec2 rayAabb(vec3 ro, vec3 invRd, vec3 amin, vec3 amax) {
	vec3 t1 = (amin - ro) * invRd;
	vec3 t2 = (amax - ro) * invRd;
	vec3 tmin = min(t1, t2);
	vec3 tmax = max(t1, t2);
	float tNear = max(max(tmin.x, tmin.y), tmin.z);
	float tFar = min(min(tmax.x, tmax.y), tmax.z);
	return vec2(tNear, tFar);
}

// Signed distance from XZ point \`p\` to a polygon defined by edges in
// \`uPolygonEdges[start .. start + count)\`. Returns negative inside, positive
// outside. Standard ray-cast parity test for inside/outside + min-distance to
// the closest edge segment for magnitude. Adapted from Inigo Quilez's
// signed-polygon-distance formulation (https://iquilezles.org/articles/distfunctions2d/).
float polygonSdf(vec2 p, int start, int count) {
	float d = 1e30;
	float s = 1.0;
	for (int k = 0; k < 64; k++) {
		if (k >= count) break;
		vec4 edge = uPolygonEdges[start + k];
		vec2 a = edge.xy;
		vec2 b = edge.zw;
		vec2 e = b - a;
		vec2 w = p - a;
		float t = clamp(dot(w, e) / dot(e, e), 0.0, 1.0);
		vec2 q = w - e * t;
		d = min(d, dot(q, q));
		// Inside/outside via edge crossings on the +X ray from p. Crossing
		// flips parity. Standard "even-odd" winding rule.
		bool crosses = (a.y > p.y) != (b.y > p.y);
		if (crosses) {
			float xCross = a.x + (p.y - a.y) * (b.x - a.x) / (b.y - a.y);
			if (xCross > p.x) s = -s;
		}
	}
	return s * sqrt(d);
}

// Sample the atmosphere along \`rayDir\` over \`dist\` meters and post-process
// identically to ProceduralSky / AerialPerspectivePass — horizon mist mix,
// exposure scale, Reinhard tonemap, day-night tint. Used twice per volume
// hit (ray entry + exit) and lerped per raymarch step; per-step sampling
// would 12× the cost for visually-identical results.
vec3 sampleAtmosphereTone(vec3 rayDir, vec3 sunDir, float dist) {
	vec3 origin = vec3(0.0, PLANET_RADIUS + 1.0, 0.0);
	vec3 atm = uSunIntensity * atmosphere(rayDir, origin, sunDir, dist);
	float horizonT = smoothstep(-0.05, 0.20, rayDir.y);
	atm = mix(mix(uFogColor, atm, horizonT), atm, 1.0 - uHorizonMist);
	atm *= uExposure;
	atm = atm / (vec3(1.0) + atm);
	return atm * uTintColor;
}

// Convert signed distance (m) to a 0..1 feather. Negative (inside, deep) → 1;
// transitions through 0 *past* the geometric edge so wisps extend a little
// beyond the polygon boundary; fully 0 once the SDF clears \`edgeFeather\`
// meters outside. The push-out is what makes the silhouette read as a cloud
// instead of a clean polygon — combined with the noise-displaced SDF below,
// the boundary becomes lumpy and organic at any zoom level.
float sdfToFeather(float sd, float edgeFeather) {
	float fw = max(edgeFeather, 0.01);
	return 1.0 - smoothstep(-fw, fw, sd);
}

void main(void) {
	if (uVolumeCount <= 0) {
		fragColor = vec4(0.0);
		return;
	}

	float depth = texture(uDepthTexture, vTexCoord).r;

	// Reversed-Z: clamp away from cleared depth (0.0 = far plane) so
	// worldPosFromDepth lands just inside the frustum on sky pixels.
	vec3 worldPosNear = worldPosFromDepth(max(depth, 0.00001), vTexCoord, uInvViewProjection);
	vec3 rayDir = normalize(worldPosNear - uCameraPosition);
	float sceneDist = (depth <= 0.0) ? SKY_DIST : length(worldPosNear - uCameraPosition);

	vec3 invRd = 1.0 / max(abs(rayDir), vec3(1e-6)) * sign(rayDir + vec3(1e-9));

	float density = 0.0;
	vec3 lit = vec3(0.0);
	float ign = fract(52.9829189 * fract(0.06711056 * gl_FragCoord.x + 0.00583715 * gl_FragCoord.y));
	float jitter = (uTaaFrameIndex == 0) ? 0.5 : fract(float(uTaaFrameIndex) * 0.61803398 + ign);

	// Per-fragment constants (independent of volume index): hoisted out of the
	// volume loop. Cloud lighting model:
	//   1. Mostly-white reflectance modulated by sun direction (forward-scatter
	//      peak when looking toward sun, ~70% on the shadow side).
	//   2. \`dayFactor\` from \`sunDirN.y\` drives day-night fade — 0 with sun
	//      below the horizon, 1 once it clears the horizon mist band. The
	//      day-night cycle modulates sun *direction*, not \`uSunIntensity\`,
	//      so direction-y is the only reliable day signal.
	//   3. \`skyAmbient\` floor tied to \`uFogColor\`, gated to night via
	//      \`(1 - dayFactor)\`, keeps low-light clouds blended with sky tint.
	//   4. Atmosphere sampled ONCE per fragment at scene distance (NOT per
	//      volume entry/exit). \`atmosphere()\` is ~3000 ops × 16 primary × 8
	//      light steps; calling it twice per volume ate frame budget on
	//      multi-volume scenes. Cloud volumes are small relative to the
	//      km-scale atmospheric column so the per-volume distance precision
	//      is invisible — the visible signal is \`rayDir\`, which doesn't vary
	//      between volumes.
	vec3 sunDirN = normalize(uSunDirection);
	float mu = dot(rayDir, sunDirN);
	float fwdScatter = 0.7 + 0.3 * smoothstep(-0.2, 1.0, mu);
	float dayFactor = smoothstep(-0.05, 0.20, sunDirN.y);
	vec3 skyAmbient = uFogColor * 0.05 * (1.0 - dayFactor);
	vec3 cloudBody = uCloudColor * fwdScatter * dayFactor + skyAmbient;
	vec3 fragAtm = sampleAtmosphereTone(rayDir, sunDirN, max(sceneDist, 1.0)) + skyAmbient;

	for (int i = 0; i < MAX_VOLUMES; i++) {
		if (i >= uVolumeCount) break;
		vec3 amin = uVolumeMin[i].xyz;
		vec3 amax = uVolumeMax[i].xyz;
		vec4 params = uVolumeParams[i];
		vec4 wind = uVolumeWind[i];
		float volDensity = params.x;
		float coverage = params.y;
		float edgeFeather = params.z;
		float noiseScale = params.w;
		int polyStart = uVolumePolygonStart[i];
		int polyCount = uVolumePolygonCount[i];

		vec2 t01 = rayAabb(uCameraPosition, invRd, amin, amax);
		float t0 = max(t01.x, 0.0);
		float t1 = min(t01.y, sceneDist);
		if (t1 <= t0) continue;

		float stepLen = (t1 - t0) / float(RAYMARCH_STEPS);
		vec2 windOffset = wind.xy * (wind.z * uTime * WIND_NOISE_SCALE);
		float baseY = amin.y;
		float topY = amax.y;
		// Vertical fade reuses \`edgeFeather\` so a single slider softens all
		// faces of the volume (top, bottom, and polygon sides). Capped at half
		// the band thickness so very thin bands still bottom out at solid
		// instead of blending across themselves; floored at 0.5m so authors
		// don't accidentally produce knife-edge horizontal slabs.
		float bandSoftness = clamp(edgeFeather, 0.5, (topY - baseY) * 0.5);

		for (int s = 0; s < RAYMARCH_STEPS; s++) {
			float t = t0 + (float(s) + jitter) * stepLen;
			vec3 p = uCameraPosition + rayDir * t;

			float vTop = 1.0 - smoothstep(topY - bandSoftness, topY, p.y);
			float vBot = smoothstep(baseY, baseY + bandSoftness, p.y);
			float vertical = vTop * vBot;
			if (vertical <= 0.001) continue;

			float sd = polygonSdf(p.xz, polyStart, polyCount);
			// Domain-warp the SDF with multi-octave noise so the polygon
			// boundary becomes lumpy / organic instead of a clean rectangle.
			// Lower-frequency octaves carve large lobes / inlets out of the
			// silhouette; higher-frequency octaves add fractal-cloud texture
			// at the edges. The noise drifts with the same wind offset as the
			// density FBM so silhouette bumps move with the clouds.
			//
			// Amplitude is generous (~3× edgeFeather plus a 25m floor) because
			// the SDF→feather smoothstep then erodes most of the displacement
			// range — without large amplitude, polygon corners on 100m+ shapes
			// remain visible as straight edges from distance.
			vec3 silSamplePos = vec3(p.xz, 0.0) * 0.03 + vec3(windOffset.x, windOffset.y, 0.0);
			float silhouetteNoise = fbm2(silSamplePos);
			float silhouetteAmp = max(edgeFeather * 3.0, 25.0);
			float displacedSdf = sd - (silhouetteNoise * 2.0 - 1.0) * silhouetteAmp;
			float horizontal = sdfToFeather(displacedSdf, edgeFeather);
			if (horizontal <= 0.001) continue;

			vec3 noisePos = p * noiseScale + vec3(windOffset.x, 0.0, windOffset.y);
			float n = fbm3(noisePos);
			float shaped = max(n - coverage, 0.0) * vertical * horizontal * volDensity;
			if (shaped <= 0.001) continue;

			float trans = exp(-shaped * stepLen);
			float weight = (1.0 - trans) * (1.0 - density);
			// Composite cloud body with distance haze. \`hazeT\` ramps from 0
			// near the camera to ~0.85 by 5km, mirroring AerialPerspectivePass'
			// density curve so cloud + opaque scene fade into the same haze
			// at distance. \`fragAtm\` is sampled once per fragment (hoisted
			// outside the volume loop) — already includes the night-floor
			// \`skyAmbient\` so distant clouds blend with sky tint at low light.
			float stepDist = t0 + (float(s) + 0.5) * stepLen;
			float hazeT = min(1.0 - exp(-max(stepDist - 100.0, 0.0) * 0.0006), 0.85);
			vec3 sampleColor = mix(cloudBody, fragAtm, hazeT);
			lit += weight * sampleColor;
			density += weight;
			if (density > 0.99) break;
		}
		if (density > 0.99) break;
	}

	if (density <= 0.001) {
		fragColor = vec4(0.0);
	} else {
		fragColor = vec4(lit, density);
	}
}
`;

    private static vertexShaderSource = `
// --- begin: fullscreen_quad.vert.glsl ---
layout(location = 0) in vec2 aVertexPosition;
out vec2 vTexCoord;

void main(void) {
	vTexCoord = aVertexPosition * 0.5 + 0.5;
	gl_Position = vec4(aVertexPosition, 0.0, 1.0);
}
// --- end: fullscreen_quad.vert.glsl ---
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: CloudVolumesShaderUniformsContainer;
    public attributesContainer: CloudVolumesShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: CloudVolumesShaderSettings) {
        super(ctx, CloudVolumesShader.fragmentShaderSource, CloudVolumesShader.vertexShaderSource, settings);
        this.uniformsContainer = new CloudVolumesShaderUniformsContainer(ctx, this);
        this.attributesContainer = new CloudVolumesShaderShaderAttributesContainer(ctx, this);
    }
}

class CloudVolumesShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: CloudVolumesShader) {
        super(ctx);
    }

    public setTaaFrameIndex(value: number) : CloudVolumesShaderUniformsContainer {
        this.setValue('uTaaFrameIndex', this.shader.uniforms.uTaaFrameIndex, value, 'int');
        return this;
    }

    public setDepthTexture(texture: Texture) : CloudVolumesShaderUniformsContainer {
        this.setTextureValue('uDepthTexture', this.shader.uniforms.uDepthTexture, texture, 'sampler2D');
        return this;
    }

    public setInvViewProjection(value: mat4 | number[]) : CloudVolumesShaderUniformsContainer {
        this.setValue('uInvViewProjection', this.shader.uniforms.uInvViewProjection, value, 'mat4');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : CloudVolumesShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setTime(value: number) : CloudVolumesShaderUniformsContainer {
        this.setValue('uTime', this.shader.uniforms.uTime, value, 'float');
        return this;
    }

    public setSunDirection(value: vec3 | number[]) : CloudVolumesShaderUniformsContainer {
        this.setValue('uSunDirection', this.shader.uniforms.uSunDirection, value, 'vec3');
        return this;
    }

    public setSunIntensity(value: number) : CloudVolumesShaderUniformsContainer {
        this.setValue('uSunIntensity', this.shader.uniforms.uSunIntensity, value, 'float');
        return this;
    }

    public setTintColor(value: vec3 | number[]) : CloudVolumesShaderUniformsContainer {
        this.setValue('uTintColor', this.shader.uniforms.uTintColor, value, 'vec3');
        return this;
    }

    public setFogColor(value: vec3 | number[]) : CloudVolumesShaderUniformsContainer {
        this.setValue('uFogColor', this.shader.uniforms.uFogColor, value, 'vec3');
        return this;
    }

    public setHorizonMist(value: number) : CloudVolumesShaderUniformsContainer {
        this.setValue('uHorizonMist', this.shader.uniforms.uHorizonMist, value, 'float');
        return this;
    }

    public setExposure(value: number) : CloudVolumesShaderUniformsContainer {
        this.setValue('uExposure', this.shader.uniforms.uExposure, value, 'float');
        return this;
    }

    public setCloudColor(value: vec3 | number[]) : CloudVolumesShaderUniformsContainer {
        this.setValue('uCloudColor', this.shader.uniforms.uCloudColor, value, 'vec3');
        return this;
    }

    public setVolumeCount(value: number) : CloudVolumesShaderUniformsContainer {
        this.setValue('uVolumeCount', this.shader.uniforms.uVolumeCount, value, 'int');
        return this;
    }

    public setVolumeMin(index: number, value: vec4 | number[]) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 8) throw new Error('Invalid index used for uVolumeMin: ' + index);
        this.setValue('uVolumeMin', this.shader.uniforms.uVolumeMin[index], value, 'vec4');
        return this;
    }

    public setVolumeMax(index: number, value: vec4 | number[]) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 8) throw new Error('Invalid index used for uVolumeMax: ' + index);
        this.setValue('uVolumeMax', this.shader.uniforms.uVolumeMax[index], value, 'vec4');
        return this;
    }

    public setVolumeParams(index: number, value: vec4 | number[]) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 8) throw new Error('Invalid index used for uVolumeParams: ' + index);
        this.setValue('uVolumeParams', this.shader.uniforms.uVolumeParams[index], value, 'vec4');
        return this;
    }

    public setVolumeWind(index: number, value: vec4 | number[]) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 8) throw new Error('Invalid index used for uVolumeWind: ' + index);
        this.setValue('uVolumeWind', this.shader.uniforms.uVolumeWind[index], value, 'vec4');
        return this;
    }

    public setVolumePolygonStart(index: number, value: number) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 8) throw new Error('Invalid index used for uVolumePolygonStart: ' + index);
        this.setValue('uVolumePolygonStart', this.shader.uniforms.uVolumePolygonStart[index], value, 'int');
        return this;
    }

    public setVolumePolygonCount(index: number, value: number) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 8) throw new Error('Invalid index used for uVolumePolygonCount: ' + index);
        this.setValue('uVolumePolygonCount', this.shader.uniforms.uVolumePolygonCount[index], value, 'int');
        return this;
    }

    public setPolygonEdges(index: number, value: vec4 | number[]) : CloudVolumesShaderUniformsContainer {
        if(index < 0 || index >= 96) throw new Error('Invalid index used for uPolygonEdges: ' + index);
        this.setValue('uPolygonEdges', this.shader.uniforms.uPolygonEdges[index], value, 'vec4');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uDepthTexture', uniform: this.shader.uniforms.uDepthTexture, dataType: 'sampler2D' }
        ];
    }
}

class CloudVolumesShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: CloudVolumesShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : CloudVolumesShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTexCoord(settings: { length: number, offset: number, itemSize: number }) : CloudVolumesShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTexCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}